import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ArrowLeft, Plus, TrashSimple, Copy, PaperPlaneTilt, CheckCircle,
    XCircle, Prohibit, Eye, ClockCounterClockwise, Shield, Sparkle,
    FilePdf, UploadSimple, Paperclip, LinkSimple, ArrowsClockwise,
    PencilSimple, Books,
} from "@phosphor-icons/react";
import { api, formatApiError, previewDocument, downloadDocument } from "../lib/api";
import { formatDate } from "../lib/format";
import { defaultSubjectForNda } from "../lib/ndaSubject";
import PageHeader from "../components/layout/PageHeader";
import StatusBadge from "../components/StatusBadge";
import CopyCodeButton from "../components/CopyCodeButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DialogFooter } from "../components/ui/dialog";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import NDAAnnexureCataloguePicker from "../components/nda/NDAAnnexureCataloguePicker";
import NDATermsConditions from "../components/nda/NDATermsConditions";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const STATUS_BADGE_MAP = {
    "Draft": { status: "Draft" },
    "Pending Approval": { status: "Open", className: "!bg-amber-50 !text-amber-800 !border-amber-200" },
    "Approved": { status: "Locked", className: "!bg-emerald-50 !text-emerald-800 !border-emerald-200" },
    "Rejected": { status: "Unpaid" },
    "Sent": { status: "Open", className: "!bg-blue-50 !text-blue-700 !border-blue-200" },
    "Signed": { status: "Active", className: "!bg-teal-50 !text-teal-800 !border-teal-200" },
    "Active": { status: "Active" },
    "Expired": { status: "Unpaid", className: "!bg-slate-100 !text-slate-700 !border-slate-200" },
    "Cancelled": { status: "Cancelled" },
};

const DISCLOSURE_MODES = [
    "Documentation Only",
    "Demonstration Only",
    "Sample Only",
    "Limited Evaluation Access",
    "Information Sharing",
    "Other",
];

function EditItemDialog({ open, onOpenChange, item, onSave }) {
    const [descOverride, setDescOverride] = useState("");
    const [mode, setMode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef({ desc: "", mode: "" });

    useEffect(() => {
        if (!open || !item) return;
        const initialDesc = item.description_override || item.description_snapshot || "";
        const initialMode = item.disclosure_mode || "";
        setDescOverride(initialDesc);
        setMode(initialMode);
        baselineRef.current = { desc: initialDesc, mode: initialMode };
    }, [open, item]);

    if (!item) return null;

    const baseDescription = item.description_snapshot || "";
    const isDirty =
        descOverride !== baselineRef.current.desc ||
        mode !== baselineRef.current.mode;

    const submit = async () => {
        setSubmitting(true);
        try {
            // Only send fields the user actually touched.
            const payload = {};
            if (descOverride !== baselineRef.current.desc) {
                // Reset override back to null when the user reverts to the
                // baseline master description exactly.
                if (descOverride.trim() === baseDescription.trim()) {
                    payload.description_override = "";
                } else {
                    payload.description_override = descOverride.trim();
                }
            }
            if (mode !== baselineRef.current.mode) {
                payload.disclosure_mode = mode || "";
            }
            await onSave(payload);
            onOpenChange(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            title="Edit Confidential Information Item"
            description="Adjust the NDA-specific description or disclosure mode.  Edits stay on THIS NDA — the Item Master is unaffected."
            testId="nda-edit-item-dialog"
            widthClass="max-w-2xl"
        >
            <div className="space-y-3">
                <div>
                    <Label>Item</Label>
                    <div className="mt-1 text-sm text-slate-900 font-medium" data-testid="nda-edit-item-name">
                        {item.item_name}
                        {item.item_group && (
                            <span className="ml-2 text-xs text-slate-500">· {item.item_group}</span>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">{item.item_code}</div>
                </div>
                <div>
                    <Label>NDA-specific Description</Label>
                    <Textarea
                        rows={4}
                        value={descOverride}
                        onChange={(e) => setDescOverride(e.target.value)}
                        placeholder="Description snapshot for this NDA"
                        data-testid="nda-edit-item-desc"
                        maxLength={500}
                    />
                    <div className="text-[11px] text-slate-500 mt-1">
                        Master baseline: <span className="italic">{baseDescription}</span>
                    </div>
                </div>
                <div>
                    <Label>Disclosure Mode (optional)</Label>
                    <select
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        className="mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-edit-item-mode"
                    >
                        <option value="">— none —</option>
                        {DISCLOSURE_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-edit-item-cancel">Cancel</FormDialogCancel>
                <Button
                    onClick={submit}
                    disabled={submitting || !isDirty}
                    data-testid="nda-edit-item-submit"
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                    {submitting ? "Saving…" : "Save changes"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

function ReviewDialog({ open, onOpenChange, nda }) {
    if (!open || !nda) return null;
    const party = nda.party_snapshot || {};
    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Review NDA"
            description="Read-only summary of the NDA that will be submitted for approval.  This is NOT the final legal document — the rendered PDF preview lands in a later phase."
            testId="nda-review-dialog"
            widthClass="max-w-3xl"
        >
            <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="reedos-eyebrow">NDA Number</div>
                        <div className="font-mono">{nda.nda_number}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">Type</div>
                        <div>{nda.nda_type} NDA</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">Subject</div>
                        <div>{nda.subject}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">Status</div>
                        <div>{nda.status}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">Effective</div>
                        <div>{formatDate(nda.effective_date)}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">Valid Until</div>
                        <div>{formatDate(nda.valid_until)}</div>
                    </div>
                    <div className="col-span-2">
                        <div className="reedos-eyebrow">Purpose</div>
                        <div className="text-slate-700 whitespace-pre-line">{nda.purpose || "—"}</div>
                    </div>
                </div>
                <div className="border-t border-slate-200 pt-3">
                    <div className="reedos-eyebrow mb-1">Party</div>
                    <div className="text-slate-900 font-medium">
                        {party.party_display_name || party.party_name}
                        {party.source === "free_form" && <span className="ml-2 text-[10px] uppercase text-slate-500">Prospective</span>}
                    </div>
                    {party.party_name && party.party_name !== party.party_display_name && (
                        <div className="text-xs text-slate-600">Legal: {party.party_name}</div>
                    )}
                    <div className="text-xs text-slate-500">
                        {[party.address_line1, party.city, party.state_name, party.country, party.pin_code].filter(Boolean).join(", ") || "—"}
                    </div>
                    {(party.contact_person || party.contact_email || party.contact_phone) && (
                        <div className="text-xs text-slate-500 mt-1">
                            {party.contact_person}
                            {party.contact_email ? ` · ${party.contact_email}` : ""}
                            {party.contact_phone ? ` · ${party.contact_phone}` : ""}
                        </div>
                    )}
                </div>
                <div className="border-t border-slate-200 pt-3">
                    <div className="reedos-eyebrow mb-2">Annexure A · Confidential Information</div>
                    {(nda.items || []).length === 0 ? (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2"
                            data-testid="nda-review-annexure-empty"
                        >
                            <strong>Annexure A is incomplete.</strong>  Add at least one
                            confidential-information item before submitting this NDA for approval.
                        </div>
                    ) : (
                        <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden" data-testid="nda-review-annexure">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-2 py-1.5 w-8 text-left">#</th>
                                    <th className="px-2 py-1.5 text-left">Item Name</th>
                                    <th className="px-2 py-1.5 text-left">Disclosure Mode</th>
                                    <th className="px-2 py-1.5 text-left">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const rows = nda.items || [];
                                    const buckets = [];
                                    const seen = new Map();
                                    for (const it of rows) {
                                        const key = (it.parent_category || "").trim();
                                        if (!seen.has(key)) {
                                            seen.set(key, buckets.length);
                                            buckets.push([key, []]);
                                        }
                                        buckets[seen.get(key)][1].push(it);
                                    }
                                    const hierarchical = buckets.some(([k]) => k);
                                    let counter = 0;
                                    return buckets.map(([parent, group]) => (
                                        <React.Fragment key={parent || "__unclassified__"}>
                                            {parent && hierarchical && (
                                                <tr
                                                    className="bg-slate-100"
                                                    data-testid={`nda-review-annexure-parent-${parent.replace(/\s+/g, "_")}`}
                                                >
                                                    <td colSpan={4} className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-700">
                                                        {parent}
                                                    </td>
                                                </tr>
                                            )}
                                            {group.map((it) => {
                                                counter += 1;
                                                return (
                                                    <tr key={it.id} className="border-t border-slate-200">
                                                        <td className="px-2 py-1.5">{counter}</td>
                                                        <td className="px-2 py-1.5 text-slate-900">
                                                            <div className="font-medium">{it.item_name}</div>
                                                            {(it.technical_name || (it.item_group && !parent)) && (
                                                                <div className="text-[10px] text-slate-500 font-mono">
                                                                    {it.technical_name
                                                                        ? `${it.technical_name}${it.primary_key ? ` · ${it.primary_key}` : ""}`
                                                                        : it.item_group}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-slate-700">{it.disclosure_mode || "—"}</td>
                                                        <td className="px-2 py-1.5 text-slate-700 whitespace-pre-line">
                                                            {it.description_override || it.description_snapshot}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="border-t border-slate-200 pt-3">
                    <div className="reedos-eyebrow mb-1">Template</div>
                    <div className="text-slate-700 text-xs">
                        {nda.template_snapshot?.template_name} v{nda.template_snapshot?.version}
                    </div>
                </div>
                <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
                    This NDA is intended solely for evaluation of a potential business relationship.
                    Signing it does NOT grant any commercial right, software licence, data licence,
                    production use, resale, redistribution or AI/ML training entitlement.  Any such
                    right requires a separate definitive agreement with REEDOS.
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-review-close">Close</FormDialogCancel>
            </DialogFooter>
        </FormDialog>
    );
}

function DecisionDialog({ open, onOpenChange, mode, onSubmit }) {
    const [remarks, setRemarks] = useState("");
    useEffect(() => { if (open) setRemarks(""); }, [open]);
    const title = mode === "approve" ? "Approve NDA"
        : mode === "reject" ? "Reject NDA"
        : mode === "cancel" ? "Cancel NDA"
        : "NDA Action";
    return (
        <FormDialog open={open} onOpenChange={onOpenChange} title={title} testId={`nda-decision-${mode}`}>
            <div>
                <Label>Remarks</Label>
                <Textarea
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder={mode === "reject" ? "Reason for rejection" : "Optional remarks"}
                    data-testid="nda-decision-remarks"
                />
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-decision-cancel">Cancel</FormDialogCancel>
                <Button
                    onClick={() => onSubmit(remarks)}
                    data-testid="nda-decision-submit"
                    className={
                        mode === "approve" ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                        : mode === "reject" ? "bg-rose-700 hover:bg-rose-800 text-white"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }
                >
                    {mode === "approve" ? "Approve" : mode === "reject" ? "Reject" : "Confirm"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

function SignedDocumentCard({ nda, canWrite, canApprove, onChange }) {
    const fileInputRef = useRef(null);
    const replaceRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const attached = Boolean(nda.signed_document_id);
    const history = nda.signed_document_history || [];

    const doUpload = async (file, { replace }) => {
        if (!file) return;
        setUploading(true);
        try {
            // 1) Upload the raw file to db.documents via the shared
            //    /billing/document/upload endpoint (entity_type=nda).
            const fd = new FormData();
            fd.append("entity_type", "nda");
            fd.append("entity_id", nda.id);
            fd.append("file", file);
            const up = await api.post("/billing/document/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // 2) Attach the resulting document to this NDA.
            const { data } = await api.post(`/billing/nda/${nda.id}/signed-document`, {
                document_id: up.data.id,
                replace,
                remarks: "",
            });
            toast.success(replace ? "Signed NDA replaced" : "Signed NDA attached");
            onChange?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setUploading(false);
        }
    };

    const doDetach = async () => {
        if (!window.confirm("Detach the current signed NDA?  The NDA will be demoted to Sent if it's Signed / Active.")) return;
        try {
            const { data } = await api.delete(`/billing/nda/${nda.id}/signed-document`, {
                data: { remarks: "" },
            });
            toast.success("Signed NDA detached");
            onChange?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Detach failed"));
        }
    };

    return (
        <div className="reedos-card p-5" data-testid="nda-signed-doc-card">
            <div className="flex items-center justify-between mb-3">
                <div className="reedos-eyebrow flex items-center gap-2"><Paperclip size={12} /> Counter-signed NDA</div>
                {canWrite && !attached && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            className="hidden"
                            onChange={(e) => doUpload(e.target.files?.[0], { replace: false })}
                            data-testid="nda-signed-upload-input"
                        />
                        <Button
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading || !["Approved", "Sent", "Signed", "Active"].includes(nda.status)}
                            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                            data-testid="nda-signed-upload-btn"
                            title={!["Approved", "Sent", "Signed", "Active"].includes(nda.status) ? "Approve the NDA first" : ""}
                        >
                            <UploadSimple size={14} /> {uploading ? "Uploading…" : "Upload Signed NDA"}
                        </Button>
                    </>
                )}
            </div>
            {!attached ? (
                <div className="text-xs text-slate-500">
                    No counter-signed document attached yet.  Upload a PDF (or image) of the signed
                    NDA to move this record to <strong>Signed</strong>.
                </div>
            ) : (
                <div className="text-sm space-y-2">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-slate-900 font-medium flex items-center gap-2">
                                <LinkSimple size={13} className="text-slate-500" />
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try { await previewDocument(nda.signed_document_id); }
                                        catch (err) { toast.error(formatApiError(err, "Preview failed")); }
                                    }}
                                    className="hover:underline text-slate-900 font-medium"
                                    data-testid="nda-signed-doc-link"
                                >
                                    Signed NDA document
                                </button>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                Attached · document id <span className="font-mono">{nda.signed_document_id}</span>
                                {nda.signed_document_signed_on ? ` · signed on ${formatDate(nda.signed_document_signed_on)}` : ""}
                            </div>
                        </div>
                        {canWrite && (
                            <div className="flex items-center gap-2">
                                <input
                                    ref={replaceRef}
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg"
                                    className="hidden"
                                    onChange={(e) => doUpload(e.target.files?.[0], { replace: true })}
                                    data-testid="nda-signed-replace-input"
                                />
                                <Button size="sm" variant="outline" onClick={() => replaceRef.current?.click()} disabled={uploading} className="gap-1.5" data-testid="nda-signed-replace-btn">
                                    <ArrowsClockwise size={14} /> Replace
                                </Button>
                                {canApprove && (
                                    <Button size="sm" variant="outline" onClick={doDetach} className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="nda-signed-detach-btn">
                                        <TrashSimple size={14} /> Detach
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {history.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">History</div>
                    <ol className="space-y-1 text-xs">
                        {history.slice().reverse().map((h) => (
                            <li key={h.id} className="text-slate-600" data-testid={`nda-signed-history-${h.id}`}>
                                <span className={
                                    h.event === "attached" ? "text-emerald-700 font-medium"
                                    : h.event === "replaced" ? "text-amber-700 font-medium"
                                    : "text-rose-700 font-medium"
                                }>{h.event}</span>
                                {" · "}{new Date(h.at).toLocaleString()}
                                {h.filename ? ` · ${h.filename}` : ""}
                                {h.replaced_document_id ? ` · replaced ${h.replaced_document_id.slice(-8)}` : ""}
                                {h.status_demoted_from ? ` · demoted from ${h.status_demoted_from}` : ""}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}

export default function NDADetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const perms = new Set(user?.permissions || []);
    const canWrite = perms.has("nda:write");
    const canApprove = perms.has("nda:approve");

    const [nda, setNda] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [decision, setDecision] = useState(null); // "approve" | "reject" | "cancel"

    const load = async () => {
        try {
            const { data } = await api.get(`/billing/nda/${id}`);
            setNda(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load NDA"));
            navigate("/ndas");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [id]);

    if (loading) return <div className="p-8 text-sm text-slate-500">Loading NDA…</div>;
    if (!nda) return null;

    const badge = STATUS_BADGE_MAP[nda.status] || { status: nda.status };
    const isDraft = nda.status === "Draft";
    const isPending = nda.status === "Pending Approval";
    const canEdit = isDraft && canWrite;

    const patchHeader = async (field, value) => {
        try {
            const { data } = await api.patch(`/billing/nda/${nda.id}`, { [field]: value });
            setNda(data);
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        }
    };

    const bulkAddItems = async (rows) => {
        try {
            const { data } = await api.post(
                `/billing/nda/${nda.id}/item/bulk-add`,
                { items: rows },
            );
            setNda(data);
            toast.success(
                rows.length === 1
                    ? "Confidential information added"
                    : `Added ${rows.length} confidential-information items`,
            );
            setPickerOpen(false);
        } catch (err) {
            toast.error(formatApiError(err, "Add failed"));
            throw err;
        }
    };
    const removeItem = async (itemId) => {
        if (!window.confirm("Remove this item?")) return;
        try {
            const { data } = await api.delete(`/billing/nda/${nda.id}/item/${itemId}`);
            setNda(data);
            toast.success("Item removed");
        } catch (err) {
            toast.error(formatApiError(err, "Remove item failed"));
        }
    };
    const editItemMode = async (itemId, mode) => {
        try {
            const { data } = await api.patch(`/billing/nda/${nda.id}/item/${itemId}`, { disclosure_mode: mode || "" });
            setNda(data);
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        }
    };
    const saveEditedItem = async (payload) => {
        if (!editingItem) return;
        try {
            const { data } = await api.patch(
                `/billing/nda/${nda.id}/item/${editingItem.id}`,
                payload,
            );
            setNda(data);
            toast.success("Item updated");
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
            throw err;
        }
    };
    const submit = async () => {
        try {
            const { data } = await api.post(`/billing/nda/${nda.id}/submit`, { remarks: "" });
            setNda(data);
            toast.success("Submitted for approval");
        } catch (err) {
            toast.error(formatApiError(err, "Submit failed"));
        }
    };
    const applyDecision = async (remarks) => {
        const route = decision === "approve" ? "approve"
            : decision === "reject" ? "reject"
            : decision === "cancel" ? "transition"
            : null;
        if (!route) return;
        try {
            const body = decision === "cancel"
                ? { to_status: "Cancelled", remarks }
                : { remarks };
            const { data } = await api.post(`/billing/nda/${nda.id}/${route}`, body);
            setNda(data);
            toast.success(`NDA ${decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "cancelled"}`);
            setDecision(null);
        } catch (err) {
            toast.error(formatApiError(err, "Action failed"));
        }
    };
    const transitionTo = async (to_status) => {
        try {
            const { data } = await api.post(`/billing/nda/${nda.id}/transition`, { to_status, remarks: "" });
            setNda(data);
            toast.success(`Moved to ${to_status}`);
        } catch (err) {
            toast.error(formatApiError(err, "Transition failed"));
        }
    };
    const duplicate = async () => {
        try {
            const { data } = await api.post(`/billing/nda/${nda.id}/duplicate`, {});
            toast.success(`Duplicated to ${data.nda_number}`);
            navigate(`/ndas/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Duplicate failed"));
        }
    };

    const generate = async () => {
        try {
            const { data } = await api.post(`/billing/nda/${nda.id}/generate`);
            if (data.already_generated) {
                toast.info("Already generated — legal snapshot preserved");
            } else {
                toast.success("Final NDA document generated and frozen");
            }
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Generate failed"));
        }
    };

    const openPreview = () => {
        window.open(`/ndas/${nda.id}/print`, "_blank", "noopener");
    };

    const downloadPDF = async () => {
        try {
            const resp = await api.get(`/billing/nda/${nda.id}/pdf`, { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `REEDOS_NDA_${(nda.nda_number || nda.id).replace(/\//g, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 503) {
                toast.warning("PDF renderer unavailable — use browser print instead");
                openPreview();
            } else {
                toast.error(formatApiError(err, "PDF download failed"));
            }
        }
    };

    const party = nda.party_snapshot || {};
    const existingMasterIds = new Set(
        (nda.items || []).map((it) => it.item_master_id).filter(Boolean),
    );

    return (
        <div data-testid="nda-detail-page">
            <PageHeader
                eyebrow="Sales · NDA"
                title={
                    <div className="flex items-center gap-3 flex-wrap">
                        <Shield size={20} weight="duotone" className="text-slate-600" />
                        <span className="font-mono text-lg">{nda.nda_number}</span>
                        <CopyCodeButton code={nda.nda_number} label="NDA number" testid="nda-header-copy" />
                        <StatusBadge {...badge}>{nda.status}</StatusBadge>
                    </div>
                }
                description={nda.subject}
                actions={
                    <div className="flex flex-wrap items-center gap-2" data-testid="nda-detail-actions">
                        <Button variant="outline" onClick={() => navigate("/ndas")} className="gap-1.5" data-testid="nda-back-btn">
                            <ArrowLeft size={14} /> Back
                        </Button>
                        <Button variant="outline" onClick={() => setReviewOpen(true)} className="gap-1.5" data-testid="nda-review-btn">
                            <Eye size={14} /> Review
                        </Button>
                        {/* Phase 2 · Legal preview + PDF */}
                        {["Approved", "Sent", "Signed", "Active", "Expired", "Cancelled"].includes(nda.status) && (
                            <>
                                <Button variant="outline" onClick={openPreview} className="gap-1.5" data-testid="nda-preview-btn">
                                    <FilePdf size={14} /> Preview Document
                                </Button>
                                {nda.generated_document ? (
                                    <Button
                                        onClick={downloadPDF}
                                        className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white"
                                        data-testid="nda-download-reedos-issued-btn"
                                        title={
                                            "REEDOS-issued NDA · frozen · SHA-256 " +
                                            String(nda.generated_document.sha256 || "").slice(0, 12) +
                                            "… · " +
                                            (nda.generated_document.origin === "baseline_capture"
                                                ? "Legacy baseline capture — original generation-time PDF bytes were not historically persisted."
                                                : "Generated and byte-frozen at generation time.")
                                        }
                                    >
                                        <FilePdf size={14} /> Download REEDOS-issued NDA (PDF)
                                    </Button>
                                ) : (
                                    ["Approved", "Sent"].includes(nda.status) && canWrite ? null : (
                                        <Button
                                            variant="outline"
                                            onClick={downloadPDF}
                                            className="gap-1.5"
                                            data-testid="nda-pdf-btn"
                                            title="Live-rendered PDF (no artifact yet frozen)"
                                        >
                                            <FilePdf size={14} /> Download PDF (live)
                                        </Button>
                                    )
                                )}
                                {nda.signed_document_id && (
                                    <Button
                                        variant="outline"
                                        onClick={async () => {
                                            try {
                                                const resp = await api.get(
                                                    `/billing/document/download/${nda.signed_document_id}`,
                                                    { responseType: "blob" },
                                                );
                                                const blob = new Blob([resp.data], { type: "application/pdf" });
                                                const url = URL.createObjectURL(blob);
                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = `SIGNED_NDA_${(nda.nda_number || nda.id).replace(/\//g, "_")}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                URL.revokeObjectURL(url);
                                            } catch (err) {
                                                toast.error(formatApiError(err, "Signed download failed"));
                                            }
                                        }}
                                        className="gap-1.5"
                                        data-testid="nda-download-executed-btn"
                                        title="Counter-signed / executed NDA uploaded by ops. Independent from the REEDOS-issued generated PDF."
                                    >
                                        <FilePdf size={14} /> Download Executed / Counter-signed NDA
                                    </Button>
                                )}
                            </>
                        )}
                        {nda.status === "Approved" && !nda.generated_document && canWrite && (
                            <Button onClick={generate} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5" data-testid="nda-generate-btn">
                                <Sparkle size={14} /> Generate Final Document
                            </Button>
                        )}
                        {canWrite && (
                            <Button variant="outline" onClick={duplicate} className="gap-1.5" data-testid="nda-duplicate-btn">
                                <Copy size={14} /> Duplicate
                            </Button>
                        )}
                        {isDraft && canWrite && (
                            <Button
                                onClick={submit}
                                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                                disabled={(nda.items || []).length === 0}
                                title={(nda.items || []).length === 0 ? "Add at least one confidential-information item first" : ""}
                                data-testid="nda-submit-btn"
                            >
                                <PaperPlaneTilt size={14} /> Submit for Approval
                            </Button>
                        )}
                        {isPending && canApprove && (
                            <>
                                <Button
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.post(
                                                `/billing/nda/${nda.id}/approval-preview`,
                                            );
                                            setNda(data);
                                            window.open(`/ndas/${nda.id}/print`, "_blank", "noopener");
                                        } catch (err) {
                                            toast.error(formatApiError(err, "Preview failed"));
                                        }
                                    }}
                                    className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                                    data-testid="nda-approval-preview-btn"
                                >
                                    <Eye size={14} /> Preview NDA PDF
                                </Button>
                                <Button
                                    onClick={() => setDecision("approve")}
                                    disabled={
                                        !nda.approval_snapshot ||
                                        nda.approval_snapshot.approval_previewed_by !== user?.id
                                    }
                                    title={
                                        (nda.approval_snapshot &&
                                            nda.approval_snapshot.approval_previewed_by === user?.id)
                                            ? ""
                                            : "Open the Preview PDF first — approver must review the exact document being approved."
                                    }
                                    className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed gap-1.5"
                                    data-testid="nda-approve-btn"
                                >
                                    <CheckCircle size={14} /> Approve
                                </Button>
                                <Button
                                    onClick={() => setDecision("reject")}
                                    className="bg-rose-700 hover:bg-rose-800 text-white gap-1.5"
                                    data-testid="nda-reject-btn"
                                >
                                    <XCircle size={14} /> Reject
                                </Button>
                            </>
                        )}
                        {nda.status === "Approved" && canWrite && (
                            <Button onClick={() => transitionTo("Sent")} className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="nda-mark-sent-btn">
                                <PaperPlaneTilt size={14} /> Mark Sent
                            </Button>
                        )}
                        {nda.status === "Sent" && canWrite && (
                            <Button onClick={() => transitionTo("Signed")} className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5" data-testid="nda-mark-signed-btn">
                                <CheckCircle size={14} /> Mark Signed
                            </Button>
                        )}
                        {nda.status === "Signed" && canWrite && (
                            <Button onClick={() => transitionTo("Active")} className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5" data-testid="nda-mark-active-btn">
                                <CheckCircle size={14} /> Mark Active
                            </Button>
                        )}
                        {nda.status === "Active" && canWrite && (
                            <Button onClick={() => transitionTo("Expired")} variant="outline" className="gap-1.5" data-testid="nda-mark-expired-btn">
                                <ClockCounterClockwise size={14} /> Mark Expired
                            </Button>
                        )}
                        {["Draft", "Pending Approval", "Approved", "Sent", "Active"].includes(nda.status) && (canWrite || canApprove) && (
                            <Button variant="outline" onClick={() => setDecision("cancel")} className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="nda-cancel-btn">
                                <Prohibit size={14} /> Cancel
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Non-commercial banner */}
                <div className="reedos-card p-3 bg-amber-50 border-amber-200 text-amber-800 text-xs" data-testid="nda-non-commercial-banner">
                    <strong>Non-commercial NDA.</strong> This document covers exchange of Confidential Information solely
                    for discussion / evaluation of a potential business opportunity.  Signing it does NOT create any
                    licence, entitlement to production use, data resale right, software distribution right or AI/ML
                    training right.  Any such right requires a separate agreement with REEDOS.
                </div>

                {/* Header */}
                <div className="reedos-card p-5" data-testid="nda-header-card">
                    <div className="reedos-eyebrow mb-3">NDA Header</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label>NDA Type</Label>
                            <div className="mt-1 text-sm text-slate-900">{nda.nda_type} NDA</div>
                        </div>
                        <div>
                            <Label>Subject</Label>
                            {canEdit ? (
                                <>
                                    <Input
                                        key={`subject-${nda.subject || ""}`}
                                        defaultValue={nda.subject}
                                        onBlur={(e) => {
                                            const raw = e.target.value;
                                            const trimmed = raw.trim();
                                            // If cleared entirely, regenerate the
                                            // auto-default from the persisted
                                            // NDA Type + Party snapshot (§ clear-
                                            // to-regenerate).  Empty default (no
                                            // party name) → keep whatever is on
                                            // record and let backend refuse a
                                            // subsequent blank submission if any.
                                            if (trimmed === "") {
                                                const auto = defaultSubjectForNda(nda);
                                                if (auto && auto !== nda.subject) {
                                                    patchHeader("subject", auto);
                                                } else {
                                                    // Repaint the input so it
                                                    // shows the persisted value.
                                                    e.target.value = nda.subject || "";
                                                }
                                                return;
                                            }
                                            if (trimmed !== (nda.subject || "").trim()) {
                                                patchHeader("subject", trimmed);
                                            }
                                        }}
                                        data-testid="nda-header-subject"
                                    />
                                    <div className="text-[11px] text-slate-500 mt-1" data-testid="nda-header-subject-hint">
                                        {(() => {
                                            const auto = defaultSubjectForNda(nda);
                                            if (!auto) return "Auto-generation unavailable — party name missing.";
                                            if ((nda.subject || "").trim() === auto) {
                                                return "Auto-generated from NDA Type + Party.  Edit to customise; clear to regenerate.";
                                            }
                                            return `Custom subject — clear the field to restore the default (${auto}).`;
                                        })()}
                                    </div>
                                </>
                            ) : <div className="mt-1 text-sm text-slate-900">{nda.subject}</div>}
                        </div>
                        <div>
                            <Label>Contact Person</Label>
                            {canEdit ? (
                                <Input
                                    defaultValue={nda.contact_person || ""}
                                    onBlur={(e) => e.target.value !== (nda.contact_person || "") && patchHeader("contact_person", e.target.value)}
                                    data-testid="nda-header-contact"
                                />
                            ) : <div className="mt-1 text-sm text-slate-900">{nda.contact_person || "—"}</div>}
                        </div>
                        <div>
                            <Label>NDA Date</Label>
                            {canEdit ? (
                                <Input type="date" defaultValue={nda.nda_date}
                                    onBlur={(e) => e.target.value !== nda.nda_date && patchHeader("nda_date", e.target.value)}
                                    data-testid="nda-header-date" />
                            ) : <div className="mt-1 text-sm text-slate-900">{formatDate(nda.nda_date)}</div>}
                        </div>
                        <div>
                            <Label>Effective Date</Label>
                            {canEdit ? (
                                <Input type="date" defaultValue={nda.effective_date}
                                    onBlur={(e) => e.target.value !== nda.effective_date && patchHeader("effective_date", e.target.value)}
                                    data-testid="nda-header-effective" />
                            ) : <div className="mt-1 text-sm text-slate-900">{formatDate(nda.effective_date)}</div>}
                        </div>
                        <div>
                            <Label>Valid Until</Label>
                            {canEdit ? (
                                <Input type="date" defaultValue={nda.valid_until}
                                    onBlur={(e) => e.target.value !== nda.valid_until && patchHeader("valid_until", e.target.value)}
                                    data-testid="nda-header-validity" />
                            ) : <div className="mt-1 text-sm text-slate-900">{formatDate(nda.valid_until)}</div>}
                        </div>
                        <div className="md:col-span-3">
                            <Label>Purpose</Label>
                            {canEdit ? (
                                <Textarea
                                    rows={2}
                                    defaultValue={nda.purpose || ""}
                                    onBlur={(e) => e.target.value !== (nda.purpose || "") && patchHeader("purpose", e.target.value)}
                                    data-testid="nda-header-purpose"
                                />
                            ) : <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">{nda.purpose || "—"}</div>}
                        </div>
                        <div className="md:col-span-3">
                            <Label>Internal Remarks</Label>
                            {canEdit ? (
                                <Input
                                    defaultValue={nda.remarks || ""}
                                    onBlur={(e) => e.target.value !== (nda.remarks || "") && patchHeader("remarks", e.target.value)}
                                    data-testid="nda-header-remarks"
                                />
                            ) : <div className="mt-1 text-sm text-slate-700">{nda.remarks || "—"}</div>}
                        </div>
                    </div>
                </div>

                {/* Party */}
                <div className="reedos-card p-5" data-testid="nda-party-card">
                    <div className="reedos-eyebrow mb-3">Party (Snapshot at Creation)</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <div className="text-slate-500 text-xs">Party Name</div>
                            <div className="text-slate-900 font-medium">
                                {party.party_display_name || party.party_name || "—"}
                                {party.source === "free_form" && (
                                    <span className="ml-2 text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Prospective</span>
                                )}
                            </div>
                            {party.party_name && party.party_name !== party.party_display_name && (
                                <div className="text-xs text-slate-500 mt-0.5">Legal: {party.party_name}</div>
                            )}
                        </div>
                        <div>
                            <div className="text-slate-500 text-xs">Contact</div>
                            <div className="text-slate-800 text-sm">{party.contact_person || "—"}</div>
                            {party.contact_email && <div className="text-xs text-slate-500">{party.contact_email}</div>}
                            {party.contact_phone && <div className="text-xs text-slate-500">{party.contact_phone}</div>}
                        </div>
                        <div>
                            <div className="text-slate-500 text-xs">Address</div>
                            <div className="text-slate-700 text-sm">
                                {[party.address_line1, party.address_line2, party.city, party.state_name, party.country, party.pin_code].filter(Boolean).join(", ") || "—"}
                            </div>
                        </div>
                    </div>
                </div>


                {/* 2026-02-25 · Terms & Conditions · per-NDA working copy */}
                <NDATermsConditions nda={nda} canEdit={canEdit} onChange={setNda} />

                {/* Items builder · Annexure A · Catalogue-driven */}
                <div className="reedos-card p-5" data-testid="nda-items-card">
                    <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                        <div>
                            <div className="reedos-eyebrow flex items-center gap-2">
                                <Books size={12} /> Annexure A · Confidential Information
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                Only Active {nda.nda_type} categories can be added.
                                Descriptions and disclosure modes are snapshotted per NDA.
                            </div>
                        </div>
                        {canEdit && (
                            <Button
                                onClick={() => setPickerOpen(true)}
                                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                                data-testid="nda-add-item-btn"
                            >
                                <Plus size={14} /> Add Confidential Information
                            </Button>
                        )}
                    </div>
                    {(nda.items || []).length === 0 ? (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-6 text-center"
                            data-testid="nda-items-empty"
                        >
                            <div className="text-sm font-semibold mb-1">Annexure A is incomplete</div>
                            <div className="text-xs mb-3">
                                Select the confidential information proposed to be disclosed under this NDA.
                            </div>
                            {canEdit && (
                                <Button
                                    size="sm"
                                    onClick={() => setPickerOpen(true)}
                                    className="bg-amber-700 hover:bg-amber-600 text-white gap-1.5"
                                    data-testid="nda-items-empty-cta"
                                >
                                    <Plus size={14} /> Add Confidential Information
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden" data-testid="nda-items-table">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-3 py-2 w-8">#</th>
                                    <th className="px-3 py-2">Item</th>
                                    <th className="px-3 py-2 w-40">Technical / Key</th>
                                    <th className="px-3 py-2 w-56">Disclosure Mode</th>
                                    <th className="px-3 py-2">NDA-Specific Description</th>
                                    {canEdit && <th className="px-3 py-2 w-24 text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Group items by parent_category, preserving
                                    // insertion order so hierarchical items surface
                                    // with a group heading while legacy items still
                                    // show flat.
                                    const rows = nda.items || [];
                                    const buckets = [];
                                    const seen = new Map();
                                    for (const it of rows) {
                                        const key = (it.parent_category || "").trim();
                                        if (!seen.has(key)) {
                                            seen.set(key, buckets.length);
                                            buckets.push([key, []]);
                                        }
                                        buckets[seen.get(key)][1].push(it);
                                    }
                                    const hierarchical = buckets.some(([k]) => k);
                                    let counter = 0;
                                    return buckets.map(([parent, group]) => (
                                        <React.Fragment key={parent || "__unclassified__"}>
                                            {parent && hierarchical && (
                                                <tr
                                                    className="bg-slate-50"
                                                    data-testid={`nda-items-parent-header-${parent.replace(/\s+/g, "_")}`}
                                                >
                                                    <td
                                                        colSpan={canEdit ? 6 : 5}
                                                        className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-700 border-t border-slate-200"
                                                    >
                                                        {parent}
                                                        <span className="ml-2 text-[10px] font-normal text-slate-500">
                                                            · {group.length} item{group.length === 1 ? "" : "s"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                            {group.map((it) => {
                                                counter += 1;
                                                return (
                                                    <tr key={it.id} className="border-t border-slate-200 align-top" data-testid={`nda-item-row-${it.id}`}>
                                                        <td className="px-3 py-2 text-slate-600">{counter}</td>
                                                        <td className="px-3 py-2">
                                                            <div className="text-slate-900 font-medium">{it.item_name}</div>
                                                            <div className="text-[10px] text-slate-400 font-mono flex items-center gap-2 flex-wrap">
                                                                <span>{it.item_code}</span>
                                                                {it.item_group && !parent && (
                                                                    <span className="text-slate-500">· {it.item_group}</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-slate-600">
                                                            {(it.technical_name || it.primary_key) ? (
                                                                <div className="font-mono text-[11px]">
                                                                    {it.technical_name && (
                                                                        <div data-testid={`nda-item-tech-${it.id}`}>{it.technical_name}</div>
                                                                    )}
                                                                    {it.primary_key && (
                                                                        <div className="text-slate-400" data-testid={`nda-item-pk-${it.id}`}>
                                                                            key · {it.primary_key}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400">{it.item_group || "—"}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {canEdit ? (
                                                                <select
                                                                    key={`${it.id}-${it.disclosure_mode || ""}`}
                                                                    defaultValue={it.disclosure_mode || ""}
                                                                    onChange={(e) => editItemMode(it.id, e.target.value)}
                                                                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                                                                    data-testid={`nda-item-mode-${it.id}`}
                                                                >
                                                                    <option value="">— none —</option>
                                                                    {DISCLOSURE_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                                                                </select>
                                                            ) : (
                                                                <span className="text-slate-700 text-xs">{it.disclosure_mode || "—"}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-slate-700 text-xs whitespace-pre-line">
                                                            {it.description_override || it.description_snapshot}
                                                            {it.description_override && (
                                                                <span className="ml-2 text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">edited</span>
                                                            )}
                                                        </td>
                                                        {canEdit && (
                                                            <td className="px-3 py-2 text-right">
                                                                <div className="inline-flex items-center gap-1.5">
                                                                    <button
                                                                        className="text-slate-500 hover:text-slate-900 p-1 rounded hover:bg-slate-100"
                                                                        onClick={() => setEditingItem(it)}
                                                                        data-testid={`nda-item-edit-${it.id}`}
                                                                        aria-label="Edit item"
                                                                        title="Edit description / disclosure mode"
                                                                    >
                                                                        <PencilSimple size={14} />
                                                                    </button>
                                                                    <button
                                                                        className="text-rose-600 hover:text-rose-800 p-1 rounded hover:bg-rose-50"
                                                                        onClick={() => removeItem(it.id)}
                                                                        data-testid={`nda-item-remove-${it.id}`}
                                                                        aria-label="Remove item"
                                                                        title="Remove item"
                                                                    >
                                                                        <TrashSimple size={14} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ));
                                })()}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>

                {/* Phase 2 · Signed document */}
                {["Approved", "Sent", "Signed", "Active", "Expired"].includes(nda.status) && (
                    <SignedDocumentCard
                        nda={nda}
                        canWrite={canWrite}
                        canApprove={canApprove}
                        onChange={setNda}
                    />
                )}

                {/* Audit history */}
                <div className="reedos-card p-5" data-testid="nda-history-card">
                    <div className="reedos-eyebrow mb-3">Activity</div>
                    <ol className="space-y-2">
                        {[...(nda.history || [])].reverse().map((h) => (
                            <li key={h.id} className="flex items-start gap-3 text-xs" data-testid={`nda-history-${h.id}`}>
                                <ClockCounterClockwise size={14} className="text-slate-400 mt-0.5" />
                                <div>
                                    <div className="text-slate-900 font-medium">{h.action.replace(/_/g, " ")}</div>
                                    <div className="text-slate-500">{new Date(h.at).toLocaleString()}</div>
                                    {h.details && Object.keys(h.details).length > 0 && (
                                        <div className="text-slate-600">{JSON.stringify(h.details)}</div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>

            <NDAAnnexureCataloguePicker
                open={pickerOpen && canEdit}
                onOpenChange={setPickerOpen}
                ndaType={nda.nda_type}
                existingMasterIds={existingMasterIds}
                onCommit={bulkAddItems}
            />
            <EditItemDialog
                open={Boolean(editingItem) && canEdit}
                onOpenChange={(o) => { if (!o) setEditingItem(null); }}
                item={editingItem}
                onSave={saveEditedItem}
            />
            <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} nda={nda} />
            <DecisionDialog
                open={Boolean(decision)}
                onOpenChange={(o) => { if (!o) setDecision(null); }}
                mode={decision}
                onSubmit={applyDecision}
            />
        </div>
    );
}
